/**
 * KartotekaFormViewsPanel — Etap 3 T3.1: zakładka „Layouty formularzy" z CRUD zamiast
 * read-only listy (create / rename / duplikuj / usuń z guardem publikacji + edycja
 * układu embedded builderem — `KartotekaViewFormBuilder`).
 *
 * TYLKO widoki FORM mają afordancje autorskie — widoki list zostają read-only
 * (`ViewsTabPanel` bez zmian w `KartotekaFieldsManager`, poza zakresem T3.1).
 *
 * Zero window.confirm — usuwanie przez `<ConfirmDialog variant="danger">` (CLAUDE.md §4.12).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KartotekaViewDto } from '@saas/shared';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Copy, Eye, Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Skeleton } from '@/components/Skeleton';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/contexts/ToastContext';
import {
  createKartotekaLibraryView,
  deleteKartotekaLibraryView,
  type KartotekaBuilderField,
  type KartotekaIdentityRef,
  kartotekaLibraryViewsQueryKey,
  updateKartotekaLibraryView,
} from '@/lib/kartoteki/kartoteka-definition';
import {
  extractLayoutSingletonTypes,
  layoutSingletonRejectedMessage,
} from '@/lib/layout-singleton-error';

import {
  CreateKartotekaViewModal,
  nameToKartotekaViewSlug,
  SLUG_MAX_LENGTH,
} from './CreateKartotekaViewModal';
import { KartotekaViewFormBuilder } from './KartotekaViewFormBuilder';

/**
 * Dokleja `_2`, `_3`, … aż slug bazowy przestanie kolidować z listą istniejących.
 * Baza jest przycinana tak, by `baza+sufiks` nie przekroczył `SLUG_MAX_LENGTH` — inaczej
 * `nameToKartotekaViewSlug` (już przycięte do 50) + sufiks mógłby dać 51-52 znaki i
 * backend odrzuciłby POST z 422 (bez retry-friendly komunikatu).
 */
function uniqueSlug(base: string, existing: readonly string[]): string {
  if (!existing.includes(base)) return base;
  let i = 2;
  let candidate: string;
  do {
    const suffix = `_${i}`;
    candidate = `${base.slice(0, SLUG_MAX_LENGTH - suffix.length)}${suffix}`;
    i++;
  } while (existing.includes(candidate));
  return candidate;
}

/** Wolna (case-insensitive) nazwa wśród `existing`; przy kolizji dokleja „ (2)", „ (3)"… */
function uniqueViewName(base: string, existing: readonly string[]): string {
  const lower = existing.map((n) => n.trim().toLowerCase());
  if (!lower.includes(base.trim().toLowerCase())) return base;
  let i = 2;
  let candidate = `${base} (${i})`;
  while (lower.includes(candidate.trim().toLowerCase())) {
    i += 1;
    candidate = `${base} (${i})`;
  }
  return candidate;
}

interface KartotekaFormViewsPanelProps {
  /** Tożsamość kartoteki (Faza 2): systemowa (slug) lub Kreatora (UUID encji). */
  identity: KartotekaIdentityRef;
  fields: KartotekaBuilderField[];
  viewsQuery: UseQueryResult<KartotekaViewDto[]>;
  readOnly: boolean;
}

export function KartotekaFormViewsPanel({
  identity,
  fields,
  viewsQuery,
  readOnly,
}: KartotekaFormViewsPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingView, setEditingView] = useState<KartotekaViewDto | null>(null);
  const [renamingSlug, setRenamingSlug] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<KartotekaViewDto | null>(null);

  const invalidateViews = () =>
    queryClient.invalidateQueries({ queryKey: kartotekaLibraryViewsQueryKey(identity) });

  const renameMutation = useMutation({
    mutationFn: ({ view, name }: { view: KartotekaViewDto; name: string }) =>
      updateKartotekaLibraryView(identity, view.slug, {
        name,
        viewType: 'form',
        config: (view.config ?? { elements: [] }) as unknown as Record<string, unknown>,
        sortOrder: view.sortOrder,
      }),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.renameViewSuccess'),
      });
      await invalidateViews();
      setRenamingSlug(null);
      setRenameError(null);
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      // Widok zapisany wcześniej z dwoma blokami „jeden na layout" nie przejdzie zapisu (nawet samej
      // nazwy) — komunikat z nazwami bloków mówi, co poprawić w układzie.
      const singletonTypes = extractLayoutSingletonTypes(err);
      toast.show({
        variant: 'error',
        title: singletonTypes
          ? layoutSingletonRejectedMessage(t, singletonTypes)
          : code === 'NAME_TAKEN'
            ? t('moduleTemplateEditor.referencePanel.viewNameTaken')
            : t('moduleTemplateEditor.referencePanel.renameViewError'),
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (source: KartotekaViewDto) => {
      const all = viewsQuery.data ?? [];
      const allSlugs = all.map((v) => v.slug);
      const formNames = all.filter((v) => v.viewType === 'form').map((v) => v.name);
      const name = uniqueViewName(`${source.name} (kopia)`, formNames);
      const slug = uniqueSlug(nameToKartotekaViewSlug(name), allSlugs);
      const clonedConfig = source.config
        ? (JSON.parse(JSON.stringify(source.config)) as Record<string, unknown>)
        : { elements: [] };
      return createKartotekaLibraryView(identity, {
        slug,
        name,
        viewType: 'form',
        config: clonedConfig,
        sortOrder: source.sortOrder,
      });
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.duplicateViewSuccess'),
      });
      await invalidateViews();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      // Kopia widoku z dwoma blokami „jeden na layout" jest odrzucana jak oryginał (patrz rename).
      const singletonTypes = extractLayoutSingletonTypes(err);
      toast.show({
        variant: 'error',
        title: singletonTypes
          ? layoutSingletonRejectedMessage(t, singletonTypes)
          : code === 'NAME_TAKEN'
            ? t('moduleTemplateEditor.referencePanel.viewNameTaken')
            : t('moduleTemplateEditor.referencePanel.duplicateViewError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (view: KartotekaViewDto) => deleteKartotekaLibraryView(identity, view.slug),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.deleteViewSuccess'),
      });
      await invalidateViews();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const messageKey =
        code === 'KARTOTEKA_VIEW_PUBLISHED_CANNOT_DELETE'
          ? 'moduleTemplateEditor.referencePanel.deleteViewPublishedError'
          : 'moduleTemplateEditor.referencePanel.deleteViewError';
      toast.show({ variant: 'error', title: t(messageKey) });
    },
  });

  if (editingView) {
    return (
      <KartotekaViewFormBuilder
        identity={identity}
        fields={fields}
        view={editingView}
        onBack={() => setEditingView(null)}
      />
    );
  }

  if (viewsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  if (viewsQuery.isError) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500">
        {t('moduleTemplateEditor.referencePanel.viewsLoadError')}
      </p>
    );
  }

  const allViews = viewsQuery.data ?? [];
  const formViews = allViews.filter((v) => v.viewType === 'form');
  const allSlugs = allViews.map((v) => v.slug);

  return (
    <div>
      {!readOnly && (
        <div className="mb-3 flex justify-end">
          <Button
            size="sm"
            variant="secondary"
            data-testid="kartoteka-new-view-btn"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('moduleTemplateEditor.referencePanel.newFormViewButton')}
          </Button>
        </div>
      )}

      {formViews.length === 0 ? (
        <p className="text-sm text-text-muted">
          {t('moduleTemplateEditor.referencePanel.noFormViews')}
        </p>
      ) : (
        <ul className="space-y-2">
          {formViews.map((v) => {
            const isRenaming = renamingSlug === v.slug;
            const isPublished = v.firstPublishedAt !== null;
            const commitRename = () => {
              const trimmed = renameValue.trim();
              if (!trimmed || trimmed === v.name) {
                setRenamingSlug(null);
                setRenameError(null);
                return;
              }
              // Unikalność nazwy tylko wśród layoutów formularza (osobna przestrzeń niż listy).
              const dup = formViews.some(
                (o) => o.slug !== v.slug && o.name.trim().toLowerCase() === trimmed.toLowerCase(),
              );
              if (dup) {
                setRenameError(t('moduleTemplateEditor.referencePanel.viewNameTaken'));
                return;
              }
              setRenameError(null);
              renameMutation.mutate({ view: v, name: trimmed });
            };
            return (
              <li
                key={v.id}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border-primary px-3 py-2 text-sm"
                data-testid="kartoteka-form-view-item"
              >
                {isRenaming ? (
                  <div className="flex w-full flex-col gap-1">
                    <Input
                      value={renameValue}
                      onChange={(e) => {
                        setRenameValue(e.target.value);
                        if (renameError) setRenameError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') {
                          // Esc anuluje zmianę nazwy — „obsłużone", okno nadrzędne się nie zamyka.
                          e.preventDefault();
                          setRenamingSlug(null);
                          setRenameError(null);
                        }
                      }}
                      onBlur={commitRename}
                      className="max-w-xs flex-1"
                      autoFocus
                      data-testid="kartoteka-view-rename-input"
                    />
                    {renameError && <p className="text-xs text-red-500">{renameError}</p>}
                  </div>
                ) : (
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate font-medium text-text-primary">{v.name}</span>
                    <code className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {v.slug}
                    </code>
                    {!readOnly && (
                      <Tooltip content={t('moduleTemplateEditor.referencePanel.renameButton')}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={t('moduleTemplateEditor.referencePanel.renameButton')}
                          data-testid="kartoteka-view-rename-btn"
                          onClick={() => {
                            setRenamingSlug(v.slug);
                            setRenameValue(v.name);
                            setRenameError(null);
                          }}
                        >
                          <Pencil />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isPublished && (
                      <Badge color="green">
                        {t('moduleTemplateEditor.referencePanel.publishedBadge')}
                      </Badge>
                    )}
                  </span>
                )}
                {!readOnly && !isRenaming && (
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Eye className="h-3.5 w-3.5" />}
                      data-testid="kartoteka-view-edit-layout-btn"
                      onClick={() => setEditingView(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.editLayoutButton')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Copy className="h-3.5 w-3.5" />}
                      disabled={duplicateMutation.isPending}
                      data-testid="kartoteka-view-duplicate-btn"
                      onClick={() => duplicateMutation.mutate(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.duplicateButton')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      disabled={isPublished}
                      title={
                        isPublished
                          ? t('moduleTemplateEditor.referencePanel.deleteViewPublishedHint')
                          : undefined
                      }
                      data-testid="kartoteka-view-delete-btn"
                      onClick={() => setPendingDelete(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.deleteViewButton')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <CreateKartotekaViewModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        identity={identity}
        existingSlugs={allSlugs}
        existingNames={formViews.map((v) => v.name)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('moduleTemplateEditor.referencePanel.deleteViewConfirmTitle')}
        message={
          pendingDelete
            ? t('moduleTemplateEditor.referencePanel.deleteViewConfirmMessage', {
                name: pendingDelete.name,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete);
            setPendingDelete(null);
          }
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
